/**
 * AssetsPage - Refactored Version
 * Uses shared components and hooks.
 */
import { useState, useMemo, useRef } from 'react';
import { assetAPI, employeeAPI, exportAPI } from '../lib/api';
import ExportButton from '../components/ExportButton';
import { useAuth } from '../context/AuthContext';
import { useApi, useMutation } from '../hooks/useApi';
import { useTable } from '../hooks/useTable';
import { formatDate, formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Loader2, UserPlus, UserMinus, Package, Laptop, Monitor, Smartphone, Box, CheckCircle, Pencil, Trash2, ChevronRight, Upload, FileText, Download, X, Mail, Clock } from 'lucide-react';

// Shared components
import { DataTable } from '../components/shared/DataTable';
import { SearchInput } from '../components/shared/SearchInput';
import { PageSkeleton } from '../components/shared/LoadingSkeleton';

// Constants
const CATEGORIES = ['Laptop', 'Desktop', 'Monitor', 'Mobile', 'Keyboard', 'Mouse', 'Chair', 'Desk', 'Other'];
const STATUS_CONFIG = {
  available: { label: 'Available', variant: 'default', icon: CheckCircle },
  assigned: { label: 'Assigned', variant: 'outline', icon: UserPlus },
  maintenance: { label: 'Maintenance', variant: 'secondary', icon: Package },
  retired: { label: 'Retired', variant: 'destructive', icon: Box },
};
const CATEGORY_ICONS = { Laptop, Desktop: Monitor, Monitor, Mobile: Smartphone };

const defaultAsset = {
  name: '', category: 'Laptop', tag_id: '', status: 'available',
  location: '', purchase_date: '', vendor: '', cost: 0, notes: '',
  serial_number: '', warranty_end_date: '',
};

export default function AssetsPage() {
  const { isAdmin, isSuperAdmin } = useAuth();
  const canManage = isAdmin || isSuperAdmin;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [formData, setFormData] = useState(defaultAsset);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Invoice upload state
  const invoiceInputRef = useRef(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  // Data fetching
  const { data: assets, loading, refresh } = useApi(assetAPI.getAll);
  const { data: employees } = useApi(employeeAPI.getAll);

  // Mutations
  const { mutate: createAsset, loading: creating } = useMutation(assetAPI.create, {
    successMessage: 'Asset added',
    onSuccess: () => { setDialogOpen(false); setFormData(defaultAsset); setSelectedAsset(null); refresh(); }
  });

  const { mutate: updateAsset, loading: updating } = useMutation(
    (data) => assetAPI.update(selectedAsset?.id, data),
    {
      successMessage: 'Asset updated',
      onSuccess: () => { setDialogOpen(false); setFormData(defaultAsset); setSelectedAsset(null); refresh(); }
    }
  );

  const { mutate: deleteAsset, loading: deleting } = useMutation(
    () => assetAPI.delete(selectedAsset?.id),
    {
      successMessage: 'Asset deleted',
      onSuccess: () => { setDeleteDialogOpen(false); setSelectedAsset(null); refresh(); }
    }
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Table hook with status filter
  const filteredByStatus = useMemo(() => {
    if (statusFilter === 'all') return assets || [];
    return (assets || []).filter(a => a.status === statusFilter);
  }, [assets, statusFilter]);

  const { items, search, setSearch, sort, setSort } = useTable(filteredByStatus, {
    searchFields: ['name', 'tag_id', 'category', 'assigned_to_name', 'location'],
    defaultSort: { field: 'created_at', order: 'desc' }
  });

  // Stats
  const stats = useMemo(() => ({
    total: (assets || []).length,
    available: (assets || []).filter(a => a.status === 'available').length,
    assigned: (assets || []).filter(a => a.status === 'assigned').length,
    maintenance: (assets || []).filter(a => a.status === 'maintenance').length,
    totalValue: (assets || []).reduce((sum, a) => sum + (a.cost || 0), 0),
  }), [assets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { 
      ...formData, 
      cost: parseFloat(formData.cost) || 0,
      // Send tag_id only if provided, otherwise let backend auto-generate
      tag_id: formData.tag_id || undefined,
      // Don't send empty strings for optional fields
      location: formData.location || undefined,
      vendor: formData.vendor || undefined,
      notes: formData.notes || undefined,
      purchase_date: formData.purchase_date || undefined,
      warranty_end_date: formData.warranty_end_date || undefined,
      serial_number: formData.serial_number || undefined,
    };
    if (selectedAsset) {
      await updateAsset(payload);
    } else {
      await createAsset(payload);
    }
  };

  // Invoice upload handler
  const handleInvoiceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedAsset) return;
    
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF and image files are allowed');
      return;
    }
    
    setUploadingInvoice(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await assetAPI.uploadInvoice(selectedAsset.id, formData);
      toast.success('Invoice uploaded successfully');
      refresh();
      // Update selected asset to show the uploaded file
      const updated = await assetAPI.get(selectedAsset.id);
      setSelectedAsset(updated.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload invoice');
    } finally {
      setUploadingInvoice(false);
      if (invoiceInputRef.current) {
        invoiceInputRef.current.value = '';
      }
    }
  };

  const handleDeleteInvoice = async () => {
    if (!selectedAsset) return;
    try {
      await assetAPI.deleteInvoice(selectedAsset.id);
      toast.success('Invoice deleted');
      refresh();
      // Update selected asset
      const updated = await assetAPI.get(selectedAsset.id);
      setSelectedAsset(updated.data);
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleEdit = (asset) => {
    setSelectedAsset(asset);
    
    // Handle purchase_date - keep as simple string if already in YYYY-MM-DD format
    let purchaseDate = '';
    if (asset.purchase_date) {
      if (typeof asset.purchase_date === 'string') {
        // Already a string - extract just the date part
        purchaseDate = asset.purchase_date.split('T')[0];
      } else {
        // It's a Date object - format without timezone issues
        purchaseDate = asset.purchase_date.toISOString().split('T')[0];
      }
    }
    
    // Handle warranty_end_date the same way
    let warrantyDate = '';
    if (asset.warranty_end_date) {
      if (typeof asset.warranty_end_date === 'string') {
        warrantyDate = asset.warranty_end_date.split('T')[0];
      } else {
        warrantyDate = asset.warranty_end_date.toISOString().split('T')[0];
      }
    }
    
    setFormData({
      name: asset.name || '',
      category: asset.category || 'Laptop',
      tag_id: asset.tag_id || '',
      status: asset.status || 'available',
      location: asset.location || '',
      purchase_date: purchaseDate,
      vendor: asset.vendor || '',
      cost: asset.cost || 0,
      notes: asset.notes || '',
      serial_number: asset.serial_number || '',
      warranty_end_date: warrantyDate,
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    await deleteAsset();
  };

  const openAddDialog = () => {
    setSelectedAsset(null);
    setFormData(defaultAsset);
    setDialogOpen(true);
  };

  // State for email notification toggle
  const [sendNotification, setSendNotification] = useState(true);

  const handleAssign = async () => {
    if (!selectedAsset || !selectedEmployee) return;
    try {
      const result = await assetAPI.assign(selectedAsset.id, selectedEmployee, sendNotification);
      if (result.data.email_sent) {
        toast.success('Asset assigned and notification email sent!');
      } else {
        toast.success('Asset assigned successfully');
      }
      setAssignDialogOpen(false);
      setSelectedAsset(null);
      setSelectedEmployee('');
      setSendNotification(true);
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign asset');
    }
  };

  const handleUnassign = async (asset) => {
    try {
      await assetAPI.unassign(asset.id);
      toast.success('Asset unassigned');
      refresh();
    } catch (error) {
      toast.error('Failed to unassign');
    }
  };

  // Table columns
  const columns = [
    { key: 'name', label: 'Asset', sortable: true, render: (row) => {
      const Icon = CATEGORY_ICONS[row.category] || Package;
      return (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-muted rounded-md"><Icon className="w-4 h-4" /></div>
          <div>
            <p className="font-medium">{row.name}</p>
            <p className="text-sm text-muted-foreground">{row.tag_id}</p>
          </div>
        </div>
      );
    }},
    { key: 'category', label: 'Category', render: (row) => <Badge variant="outline">{row.category}</Badge> },
    { key: 'status', label: 'Status', render: (row) => {
      const config = STATUS_CONFIG[row.status] || STATUS_CONFIG.available;
      return <Badge variant={config.variant}>{config.label}</Badge>;
    }},
    { key: 'assigned_to_name', label: 'Assigned To', render: (row) => {
      if (!row.assigned_to_name) return '-';
      return (
        <div className="flex items-center gap-2">
          <span>{row.assigned_to_name}</span>
          {row.status === 'assigned' && (
            row.assignment_accepted === true ? (
              <span title="Accepted" className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                <CheckCircle className="w-3 h-3 mr-0.5" /> Accepted
              </span>
            ) : row.assignment_accepted === false ? (
              <span title="Issue Reported" className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700">
                Issue
              </span>
            ) : (
              <span title="Pending Acceptance" className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700">
                <Clock className="w-3 h-3 mr-0.5" /> Pending
              </span>
            )
          )}
        </div>
      );
    }},
    { key: 'purchase_date', label: 'Purchase Date', sortable: true, render: (row) => row.purchase_date ? formatDate(row.purchase_date) : '-' },
    { key: 'location', label: 'Location', render: (row) => row.location || '-' },
    { key: 'cost', label: 'Value', sortable: true, render: (row) => formatCurrency(row.cost) },
    { key: 'actions', label: '', render: (row) => canManage && (
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleEdit(row); }} title="Edit">
          <Pencil className="w-4 h-4" />
        </Button>
        {row.status === 'available' && (
          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setSelectedAsset(row); setAssignDialogOpen(true); }} data-testid={`assign-btn-${row.id}`}>
            <UserPlus className="w-4 h-4 mr-1" /> Assign
          </Button>
        )}
        {row.status === 'assigned' && (
          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleUnassign(row); }}>
            <UserMinus className="w-4 h-4 mr-1" /> Unassign
          </Button>
        )}
        <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={(e) => { e.stopPropagation(); setSelectedAsset(row); setDeleteDialogOpen(true); }} title="Delete">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    )}
  ];

  if (loading) return <PageSkeleton />;

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Asset Management</h1>
          <p className="text-muted-foreground mt-1">Track and manage company assets</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton 
            exportFn={exportAPI.assets} 
            filename="assets.csv" 
            label="Export"
          />
          {canManage && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full" onClick={openAddDialog}><Plus className="w-4 h-4 mr-2" /> Add Asset</Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{selectedAsset ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
                <DialogDescription>{selectedAsset ? 'Update asset details' : 'Enter asset details'}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Asset Name <span className="text-red-500">*</span></Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="asset-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Tag ID <span className="text-muted-foreground text-xs">(auto-generated if empty)</span></Label>
                    <Input value={formData.tag_id} onChange={(e) => setFormData({ ...formData, tag_id: e.target.value })} placeholder="Leave empty to auto-generate" data-testid="asset-tag" />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                      <SelectTrigger data-testid="asset-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Cost (₹)</Label>
                    <Input type="number" value={formData.cost} onChange={(e) => setFormData({ ...formData, cost: e.target.value })} data-testid="asset-cost" />
                  </div>
                  <div className="space-y-2">
                    <Label>Purchase Date</Label>
                    <Input type="date" value={formData.purchase_date} onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })} data-testid="asset-purchase-date" />
                  </div>
                  <div className="space-y-2">
                    <Label>Warranty End Date</Label>
                    <Input type="date" value={formData.warranty_end_date} onChange={(e) => setFormData({ ...formData, warranty_end_date: e.target.value })} data-testid="asset-warranty" />
                  </div>
                  <div className="space-y-2">
                    <Label>Serial Number</Label>
                    <Input value={formData.serial_number} onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })} placeholder="Enter serial number" data-testid="asset-serial" />
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} data-testid="asset-location" />
                  </div>
                  <div className="space-y-2">
                    <Label>Vendor</Label>
                    <Input value={formData.vendor} onChange={(e) => setFormData({ ...formData, vendor: e.target.value })} data-testid="asset-vendor" />
                  </div>
                </div>
                
                {/* Invoice Upload - only show when editing */}
                {selectedAsset && (
                  <div className="space-y-2 border rounded-lg p-4 bg-muted/30">
                    <Label className="flex items-center gap-2"><FileText className="w-4 h-4" /> Purchase Invoice / Document</Label>
                    <input
                      ref={invoiceInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleInvoiceUpload}
                      className="hidden"
                      data-testid="asset-invoice-input"
                    />
                    {selectedAsset.invoice_url ? (
                      <div className="flex items-center gap-2 p-2 bg-background rounded border">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <span className="flex-1 text-sm truncate">{selectedAsset.invoice_filename || 'Invoice attached'}</span>
                        <Button type="button" size="sm" variant="outline" onClick={() => window.open(`${window.location.origin}${selectedAsset.invoice_url}`, '_blank')}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button type="button" size="sm" variant="ghost" className="text-destructive" onClick={handleDeleteInvoice}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => invoiceInputRef.current?.click()}
                        disabled={uploadingInvoice}
                        className="w-full"
                        data-testid="upload-invoice-btn"
                      >
                        {uploadingInvoice ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {uploadingInvoice ? 'Uploading...' : 'Upload Invoice (PDF/Image)'}
                      </Button>
                    )}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} data-testid="asset-notes" />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={creating || updating} data-testid="asset-submit">
                    {(creating || updating) ? <Loader2 className="w-4 h-4 animate-spin" /> : selectedAsset ? 'Update Asset' : 'Add Asset'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all" onClick={() => setStatusFilter('all')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div><div className="text-muted-foreground text-sm mb-1">Total Assets</div><p className="text-2xl font-bold">{stats.total}</p></div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all" onClick={() => setStatusFilter('available')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div><div className="text-green-600 text-sm mb-1">Available</div><p className="text-2xl font-bold">{stats.available}</p></div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all" onClick={() => setStatusFilter('assigned')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div><div className="text-blue-600 text-sm mb-1">Assigned</div><p className="text-2xl font-bold">{stats.assigned}</p></div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all" onClick={() => setStatusFilter('maintenance')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div><div className="text-yellow-600 text-sm mb-1">Maintenance</div><p className="text-2xl font-bold">{stats.maintenance}</p></div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-muted-foreground text-sm mb-1">Total Value</div>
            <p className="text-2xl font-bold">{formatCurrency(stats.totalValue)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <SearchInput value={search} onChange={setSearch} placeholder="Search assets..." className="flex-1 max-w-md" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Filter by status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="maintenance">Maintenance</SelectItem>
            <SelectItem value="retired">Retired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <DataTable data={items} columns={columns} sort={sort} onSort={setSort} emptyMessage="No assets found" emptyIcon={<Package className="h-12 w-12 text-muted-foreground" />} />
        </CardContent>
      </Card>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Asset</DialogTitle>
            <DialogDescription>Assign {selectedAsset?.name} to an employee</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Employee</Label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger data-testid="assign-employee-select"><SelectValue placeholder="Choose employee" /></SelectTrigger>
                <SelectContent>
                  {(employees || []).filter(e => e.status === 'active').map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name} ({e.department})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <input
                type="checkbox"
                id="send-notification"
                checked={sendNotification}
                onChange={(e) => setSendNotification(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                data-testid="send-notification-checkbox"
              />
              <label htmlFor="send-notification" className="text-sm">
                <span className="font-medium text-blue-800">Send email notification</span>
                <p className="text-xs text-blue-600 mt-0.5">Employee will receive an email to accept/reject the allocation</p>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={!selectedEmployee} data-testid="confirm-assign-btn">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Asset</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{selectedAsset?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
